import React, { useState, useEffect, useRef, useCallback } from 'react';
import { database } from '../firebaseConfig';
import { ref, get, update } from 'firebase/database';
import {
  IG_RESEARCH_SETTINGS_PATH,
  IG_RESEARCH_DEFAULT_LANGUAGE,
  IG_RESEARCH_LANGUAGE_OPTIONS,
  languageFromIgSettings,
} from './instagramResearchLanguage';
import './LeadSpySettings.css';

const DEBOUNCE_MS = 600;

const InstagramResearchSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [language, setLanguage] = useState(IG_RESEARCH_DEFAULT_LANGUAGE);

  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!userId) return undefined;
    setLoading(true);
    hasLoadedRef.current = false;
    get(ref(database, IG_RESEARCH_SETTINGS_PATH(userId))).then((snapshot) => {
      if (snapshot.exists()) {
        setLanguage(languageFromIgSettings(snapshot.val()));
      }
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    }).catch(() => {
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    });
  }, [userId]);

  const persist = useCallback(async (nextLanguage) => {
    if (!userId) return;
    setSaveStatus('saving');
    try {
      await update(ref(database, IG_RESEARCH_SETTINGS_PATH(userId)), {
        language: nextLanguage,
        updatedAt: Date.now(),
      });
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } catch (error) {
      console.error('Error saving Instagram research settings:', error);
      setSaveStatus('idle');
    }
  }, [userId]);

  useEffect(() => {
    if (!hasLoadedRef.current) return undefined;
    clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => {
      persist(language);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [language, persist]);

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(savedTimerRef.current);
  }, []);

  if (loading) {
    return (
      <div className="spySettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <div className="igResearchSettings">
      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-globe"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>Save language</h5>
            <p>Instagram insights written into the contact description</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="languageOptions">
            {IG_RESEARCH_LANGUAGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`langOption ${language === opt.id ? 'active' : ''}`}
                onClick={() => setLanguage(opt.id)}
              >
                {opt.flag} {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`spyAutoSaveIndicator ${saveStatus}`}>
        {saveStatus === 'saving' && (
          <>
            <span className="autoSaveSpinner"></span>
            <span>Saving…</span>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <i className="fa fa-check"></i>
            <span>Saved</span>
          </>
        )}
      </div>
    </div>
  );
};

export default InstagramResearchSettings;
